import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Typography } from 'antd';
import moment from 'moment';
import { PmsAPI } from '../../../../controller/PmsAPI';
import RebalanceList from './RebalanceList';
import {
  readExcludedRoomTypes,
  readRebalanceAction,
  readRebalanceMinUnsold,
  REBALANCE_ACTION_ALL,
  writeExcludedRoomTypes,
  writeRebalanceAction,
  writeRebalanceMinUnsold,
} from './listPreferences';
import {
  buildDateColumns,
  buildRebalanceList,
  buildRoomTypeRows,
  DATE_FORMAT,
  REBALANCE_DAYS,
  REBALANCE_MIN_UNSOLD,
} from '../InventoryPage/inventoryData';

const { Text, Title } = Typography;

/**
 * 待調控清單。與「房型庫存查詢」共用同一支庫存 API 與同一套判定規則
 * (inventoryData 的 rebalanceHint)，只是換一種呈現：
 * 矩陣適合看某段期間的全貌，清單適合照日期逐項處理。
 *
 * 獨立成一頁而非放在庫存查詢的分頁裡 —— 這是訂房組每天要走一遍的作業，
 * 藏在別的頁面底下會讓人忘記它存在，也沒辦法直接給連結。
 */
export default function RebalancePage() {
  const [state, setState] = useState({
    loading: true,
    items: [],
    roomTypes: [],
    range: null,
    error: null,
  });
  const [days, setDays] = useState(REBALANCE_DAYS);
  // 排除的房型代碼。存進 localStorage —— 不打算上線賣的房型每次都要重選一次的話，
  // 這個功能等於沒有用，訂房組是每天回來看同一份清單的
  const [excluded, setExcluded] = useState(readExcludedRoomTypes);
  // 建議篩選同理 —— 訂房組會分工，有人只處理釋出、有人只處理加配額
  const [action, setAction] = useState(readRebalanceAction);
  // 未售數量門檻同理 —— 幾間才值得動手是每個人自己的標準
  const [minUnsold, setMinUnsold] = useState(readRebalanceMinUnsold);

  const query = useCallback(async (targetDays) => {
    const from = moment();
    const to = moment().add(targetDays - 1, 'days');
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await PmsAPI.inventory.index(
        from.format(DATE_FORMAT),
        to.format(DATE_FORMAT)
      );
      const cols = buildDateColumns(
        from.format(DATE_FORMAT),
        to.format(DATE_FORMAT),
        response.data.days
      );
      const rows = buildRoomTypeRows(cols, response.data.days, response.data.room_types);
      setState({
        loading: false,
        items: buildRebalanceList(cols, rows),
        // 房型選項取自 API 而非清單本身 —— 若只列出「目前有待調控項目」的房型，
        // 被排除的房型會從選單裡消失，使用者就取消不掉自己設的排除
        roomTypes: response.data.room_types || [],
        range: [from, to],
        error: null,
      });
    } catch (e) {
      // 失敗時清掉舊清單 —— 留著過期資料比顯示錯誤更危險，訂房組會照著它去改配置。
      // roomTypes 保留，排除選單才不會在錯誤畫面上變成空的
      setState((prev) => ({
        ...prev,
        loading: false,
        items: [],
        range: null,
        error: e?.response?.data?.message || '待調控清單查詢失敗',
      }));
    }
  }, []);

  // 換天數就重查 —— setState 後 days 尚未更新，故把新值直接傳進去
  const handleDaysChange = useCallback((next) => {
    setDays(next);
    query(next);
  }, [query]);

  // 排除房型只是前端過濾，不必重打 API
  const handleExcludedChange = useCallback((next) => {
    const value = next || [];
    setExcluded(value);
    writeExcludedRoomTypes(value);
  }, []);

  const handleActionChange = useCallback((next) => {
    const value = next || REBALANCE_ACTION_ALL;
    setAction(value);
    writeRebalanceAction(value);
  }, []);

  // 清空輸入框時 antd 會送 null 進來，那不是一個門檻 —— 忽略掉，
  // 保留上一個值直到使用者打完新的數字，否則打字打到一半清單就先亂跳
  const handleMinUnsoldChange = useCallback((next) => {
    if (!Number.isFinite(next)) return;
    const value = Math.max(REBALANCE_MIN_UNSOLD, Math.floor(next));
    setMinUnsold(value);
    writeRebalanceMinUnsold(value);
  }, []);

  useEffect(() => {
    query(REBALANCE_DAYS);
  }, [query]);

  // 三道過濾分開算，才講得出各自藏了幾筆 —— 合成一個數字的話，
  // 使用者看到「隱藏 8 筆」不知道該去動哪一個條件
  const roomTypeVisible = useMemo(
    () => state.items.filter((item) => !excluded.includes(item.cod)),
    [state.items, excluded]
  );

  const actionVisible = useMemo(
    () => (action === REBALANCE_ACTION_ALL
      ? roomTypeVisible
      : roomTypeVisible.filter((item) => item.action === action)),
    [roomTypeVisible, action]
  );

  const visibleItems = useMemo(
    () => actionVisible.filter((item) => item.unsold >= minUnsold),
    [actionVisible, minUnsold]
  );

  return (
    <Card>
      <Title level={4}>待調控清單</Title>

      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          房還有得賣、卻有一邊通路已無庫存的房型日，逐項列出供訂房組處理。
          <Text strong>現場可售為 0</Text> 代表房都押在線上，櫃台與直訂接不了單；
          <Text strong>線上銷售為 0</Text> 代表所有線上通路 (含官網) 都已無房可賣。
          兩者都會少賣。
          同樣的判定在「房型庫存查詢」裡是標成橘底的格子，要看某段期間的前後脈絡請用那一頁。
        </Text>
      </div>

      <RebalanceList
        items={visibleItems}
        hiddenCount={state.items.length - roomTypeVisible.length}
        actionHiddenCount={roomTypeVisible.length - actionVisible.length}
        minUnsoldHiddenCount={actionVisible.length - visibleItems.length}
        loading={state.loading}
        range={state.range}
        error={state.error}
        days={days}
        onDaysChange={handleDaysChange}
        action={action}
        onActionChange={handleActionChange}
        minUnsold={minUnsold}
        onMinUnsoldChange={handleMinUnsoldChange}
        roomTypes={state.roomTypes}
        excluded={excluded}
        onExcludedChange={handleExcludedChange}
        onRefresh={() => query(days)}
      />
    </Card>
  );
}
